/**
 * Interface language.
 *
 * English is the source language: every key is defined in `EN`, and that is what
 * a new install shows regardless of the browser's locale. German is an overlay —
 * a key it does not carry falls back to the English one, so a half-finished
 * translation degrades to readable English rather than to a blank or a key name.
 *
 * The overlay deliberately covers the everyday surface only: the list, the chat,
 * the composer, the settings, the dialogs. Transport and Gateway errors, tool
 * names, and the origin troubleshooting screen stay English in both languages —
 * they are the strings a person copies into an issue or a terminal, and one
 * wording for them is worth more than a translation.
 *
 * `{name}` placeholders are substituted from the `vars` argument.
 */

export type Language = "en" | "de";

export const LANGUAGES: readonly Language[] = ["en", "de"];

export const LANGUAGE_NAMES: Record<Language, string> = {
  en: "English",
  de: "Deutsch",
};

const EN = {
  // --- conversation list ---------------------------------------------------
  "list.title": "Chats",
  "list.settings": "Settings",
  "list.compose": "New conversation",
  "list.search": "Search",
  "list.searchLabel": "Search conversations",
  "list.scopeActive": "Active",
  "list.scopeArchived": "Archive",
  "list.pinned": "pinned",
  "list.noMessages": "No messages yet",
  "list.working": "OpenClaw is working …",
  "list.inMessages": "In messages",
  "list.indexing": "The Gateway is still building its search index.",
  "list.fromYou": "You: ",
  "list.cronHiddenOne": "1 empty cron chat hidden",
  "list.cronHiddenMany": "{count} empty cron chats hidden",
  "list.cronShown": "Hide empty cron chats",
  "list.loading": "Loading chats …",
  "list.noMatchTitle": "Nothing found",
  "list.noMatchBody": "No conversation matches “{query}”.",
  "list.archiveEmptyTitle": "Archive is empty",
  "list.archiveEmptyBody": "Archived conversations appear here.",
  "list.emptyTitle": "No chats yet",
  "list.emptyBody":
    "Start your first conversation — every chat is its own OpenClaw session with its own context.",

  // --- chat ----------------------------------------------------------------
  "chat.back": "Back to overview",
  "chat.overview": "Overview",
  "chat.goneTitle": "This conversation is no longer here",
  "chat.goneBody": "It was archived or deleted. Go back to the overview to open another one.",
  "chat.noneTitle": "No chat selected",
  "chat.noneBody": "Pick a conversation on the left, or start a new one.",
  "chat.options": "Chat options",
  "chat.connected": "connected",
  "chat.disconnected": "not connected",
  "chat.working": "OpenClaw is working …",
  "chat.defaultModel": "Default model",
  "chat.dropHint": "Drop the image here",
  "chat.loadingHistory": "Loading history …",
  "chat.emptyBody": "Write the first message — this chat has its own context.",
  "chat.loadOlder": "Load older messages",
  "chat.loadingOlder": "Loading …",
  "chat.jumpLatest": "Jump to the newest messages",
  "chat.composerPlaceholder": "Write a message …",
  "chat.composerDisconnected": "Not connected",

  // --- turn steps ----------------------------------------------------------
  "steps.title": "Intermediate steps",
  "steps.show": "Show steps",
  "steps.hide": "Hide steps",
  "steps.options": "Step options",
  "steps.running": "running …",
  "steps.one": "1 step",
  "steps.many": "{count} steps",
  "steps.thoughts": "Thoughts",
  "steps.toolOutput": "Tool output",

  // --- message bubbles -----------------------------------------------------
  "bubble.running": "running …",
  "bubble.error": "Error",
  "bubble.more": "more",
  "bubble.less": "less",
  "bubble.notSent": "not sent",
  "bubble.sending": "sending …",
  "bubble.workingPlain": "OpenClaw is working …",
  "bubble.workingPhase": "OpenClaw is working — {phase} …",
  "phase.queued": "queued",
  "phase.waiting_for_slot": "waiting for a free slot",
  "phase.provisioning_environment": "preparing the environment",
  "phase.preparing_context": "gathering the context",
  "phase.starting_model": "starting the model",

  // --- attachments ---------------------------------------------------------
  "attachment.image": "Image",
  "attachment.file": "File",
  "attachment.unavailable": "Image not available",
  "attachment.loading": "Loading image …",
  "attachment.remove": "Remove",
  "attachment.previewImage": "📷 Image",
  "attachment.previewFile": "📎 {name}",
  "attachment.redactedThinking": "(withheld by the provider)",

  // --- composer ------------------------------------------------------------
  "composer.attach": "Attach image",
  "composer.message": "Message",
  "composer.stop": "Stop the reply",
  "composer.send": "Send",
  "composer.queueLabel": "While OpenClaw is working",
  "composer.steer": "Chime in",
  "composer.steerHint": "Folds into the turn that is running",
  "composer.followup": "Queue",
  "composer.followupHint": "Takes its turn once this one is done",
  "composer.interrupt": "Interrupt",
  "composer.interruptHint": "Stops the running turn and starts over",

  // --- day and time labels -------------------------------------------------
  "time.today": "Today",
  "time.yesterday": "Yesterday",

  // --- compose dialog ------------------------------------------------------
  "compose.title": "New conversation",
  "compose.description": "Every chat is its own OpenClaw session with its own context.",
  "compose.titleLabel": "Title (optional)",
  "compose.titlePlaceholder": "e.g. Holiday planning",
  "compose.titleHint": "Without a title OpenClaw names the chat after the first message.",
  "compose.messageLabel": "First message (optional)",
  "compose.messagePlaceholder": "Find me hotels in Prague.",
  "compose.submit": "Start",
  "compose.submitting": "Creating …",

  // --- rename dialog -------------------------------------------------------
  "rename.title": "Rename chat",
  "rename.label": "Title",
  "rename.hint": "Leave empty to go back to the automatic title.",

  // --- chat menu -----------------------------------------------------------
  "menu.rename": "Rename",
  "menu.pin": "Pin",
  "menu.unpin": "Unpin",
  "menu.archive": "Archive",
  "menu.restore": "Restore",
  "menu.model": "Change model",
  "menu.speak": "Read the last answer aloud",
  "menu.delete": "Delete",

  // --- delete confirmation -------------------------------------------------
  "delete.title": "Delete chat?",
  "delete.description":
    "The conversation and its history are removed for good. This cannot be undone.",
  "delete.confirm": "Delete permanently",

  // --- shared buttons ------------------------------------------------------
  "action.cancel": "Cancel",
  "action.save": "Save",
  "action.copy": "copy",
  "action.copied": "copied",
  "action.copyToClipboard": "Copy to the clipboard",
  "action.close": "Close",

  // --- model picker --------------------------------------------------------
  "model.title": "Model",
  "model.description": "Applies to “{title}” only.",
  "model.agentDefault": "The agent’s default",
  "model.agentDefaultMeta": "No fixed model for this chat",
  "model.loading": "Loading models …",
  "model.none": "This Gateway names no selectable models.",
  "model.context": " · {size}k context",
  "model.reasoning": " · reasoning",
  "model.unavailable": " · unavailable",

  // --- settings ------------------------------------------------------------
  "settings.title": "Settings",
  "settings.language": "Language",
  "settings.theme": "Appearance",
  "settings.themeSystem": "System",
  "settings.themeLight": "Light",
  "settings.themeDark": "Dark",
  "settings.enter": "Enter key",
  "settings.enterAuto": "Automatic",
  "settings.enterSend": "Send",
  "settings.enterNewline": "New line",
  "settings.enterAutoHint":
    "With a keyboard Enter sends; on a touchscreen it starts a new line.",
  "settings.enterSendHint": "Enter sends, Shift+Enter starts a new line.",
  "settings.enterNewlineHint": "Enter starts a new line; sending goes through the button.",
  "settings.install": "Install the app",
  "settings.installed": "cllw is running as an installed app.",
  "settings.installAction": "Install on this device",
  "settings.installSafari":
    "In Safari tap <strong>Share</strong> at the bottom, then <strong>Add to Home Screen</strong>. On an iPhone that is also what notifications require.",
  "settings.installManual":
    "This browser does not offer an install — its own menu usually still does (“Add to Home Screen”, “Install app”).",
  "settings.notifications": "Notifications",
  "settings.pushBusy": "One moment …",
  "settings.pushEnable": "Turn notifications on",
  "settings.pushDisable": "Turn notifications off",
  "settings.pushTest": "Send a test notification",
  "settings.pushHint":
    "The Gateway sends them straight to this device — even when cllw is closed.",
  "settings.pushUnsupported": "This browser cannot do notifications.",
  "push.setupTitle": "Your Gateway is not set up for this yet",
  "push.setupWhat":
    "The notifications are sent by your Gateway, and for that it needs a signing key pair of its own — the VAPID key. It has none yet, so the browser has nothing to check the sender against and turns the registration down.",
  "push.setupAsk":
    "Quickest from here: ask OpenClaw. It is allowed to edit its own configuration — write to it wherever you already reach it.",
  "push.setupPrompt":
    "Please set up Web Push on the Gateway: generate a VAPID key pair, put it into the Gateway configuration, and restart the Gateway afterwards. It worked once push.web.vapidPublicKey returns the public key.",
  "push.setupAfter":
    "The Gateway is away for a moment while it restarts. After that, tap “Turn notifications on” again.",
  "settings.gateway": "Gateway",
  "settings.status": "Status",
  "settings.statusConnected": "connected",
  "settings.scopes": "This device’s rights",
  "settings.scopesHint":
    "You granted these rights while pairing. Changing them is only possible in OpenClaw itself.",
  "settings.deviceId": "Device ID",
  "settings.disconnect": "Disconnect and sign this device out",
  "scope.operator.read": "Read chats and history",
  "scope.operator.write": "Send messages, manage chats",
  "scope.operator.admin": "Delete chats for good",
  "scope.operator.pairing": "Manage devices",
  "scope.operator.talk": "Voice sessions",

  // --- update prompt -------------------------------------------------------
  "update.available": "A new version is available.",
  "update.reload": "Reload",

  // --- setup ---------------------------------------------------------------
  "setup.lead": "Connect to your OpenClaw Gateway once. After that the app stays signed in.",
  "setup.urlLabel": "Gateway address",
  "setup.urlHint":
    "For example <code>ws://192.168.1.20:18789</code> on your own network, or <code>wss://gateway.example</code> from outside.",
  "setup.tokenLabel": "Token",
  "setup.tokenPlaceholder": "Gateway token",
  "setup.tokenHint":
    "Leave it empty if your Gateway runs without a token. This device then signs in as a device of its own — you approve it once in OpenClaw.",
  "setup.submit": "Connect",
  "setup.submitting": "Connecting …",
  "setup.badUrl": "Invalid address.",
  "setup.failed": "Connection failed.",

  // --- pairing -------------------------------------------------------------
  "pairing.titleUpgrade": "Approve extended rights",
  "pairing.title": "Approve this device",
  "pairing.leadUpgrade":
    "This device is signing in to <strong>{url}</strong> with changed details. You confirm that once more in OpenClaw.",
  "pairing.lead":
    "The connection to <strong>{url}</strong> is up. This device is now waiting for your one-time approval in OpenClaw.",
  "pairing.step1": "Run this on the Gateway machine",
  "pairing.noRequestId":
    "The Gateway did not send a request id. <code>openclaw devices list</code> shows the open request.",
  "pairing.step2": "Done",
  "pairing.step2Hint":
    "The app checks by itself every few seconds and starts the moment you have confirmed.",
  "pairing.requestId": "Request ID",
  "pairing.deviceId": "Device ID",
  "pairing.requestedScopes": "Requested rights",
  "pairing.waiting": "Waiting for approval …",
  "pairing.otherConnection": "Different connection",
  "pairing.checkNow": "Check now",

  // --- connection banner ---------------------------------------------------
  "banner.ok": "OK",
  "banner.pairing": "Waiting for device approval in OpenClaw …",
  "banner.connecting": "Connecting to the Gateway …",
  "banner.reconnecting": "Connection lost — trying again …",
  "banner.failed": "Connection failed.",
} as const;

export type MessageKey = keyof typeof EN;

/**
 * German overlay. Anything absent here is shown in English on purpose — see the
 * module comment for what that covers and why.
 */
const DE: Partial<Record<MessageKey, string>> = {
  "list.title": "Chats",
  "list.settings": "Einstellungen",
  "list.compose": "Neue Unterhaltung",
  "list.search": "Suchen",
  "list.searchLabel": "Unterhaltungen durchsuchen",
  "list.scopeActive": "Aktiv",
  "list.scopeArchived": "Archiv",
  "list.pinned": "angeheftet",
  "list.noMessages": "Noch keine Nachrichten",
  "list.working": "OpenClaw arbeitet …",
  "list.inMessages": "In Nachrichten",
  "list.indexing": "Das Gateway baut seinen Suchindex noch auf.",
  "list.fromYou": "Du: ",
  "list.cronHiddenOne": "1 leerer Cron-Chat ausgeblendet",
  "list.cronHiddenMany": "{count} leere Cron-Chats ausgeblendet",
  "list.cronShown": "Leere Cron-Chats ausblenden",
  "list.loading": "Chats werden geladen …",
  "list.noMatchTitle": "Nichts gefunden",
  "list.noMatchBody": "Keine Unterhaltung passt zu „{query}“.",
  "list.archiveEmptyTitle": "Archiv ist leer",
  "list.archiveEmptyBody": "Archivierte Unterhaltungen erscheinen hier.",
  "list.emptyTitle": "Noch keine Chats",
  "list.emptyBody":
    "Starte deine erste Unterhaltung — jeder Chat ist eine eigene OpenClaw-Session mit eigenem Kontext.",

  "chat.back": "Zurück zur Übersicht",
  "chat.overview": "Übersicht",
  "chat.goneTitle": "Diese Unterhaltung ist nicht mehr hier",
  "chat.goneBody":
    "Sie wurde archiviert oder gelöscht. Zurück zur Übersicht, um eine andere zu öffnen.",
  "chat.noneTitle": "Kein Chat ausgewählt",
  "chat.noneBody": "Wähle links eine Unterhaltung oder starte eine neue.",
  "chat.options": "Chat-Optionen",
  "chat.connected": "verbunden",
  "chat.disconnected": "nicht verbunden",
  "chat.working": "OpenClaw arbeitet …",
  "chat.defaultModel": "Standardmodell",
  "chat.dropHint": "Bild hier ablegen",
  "chat.loadingHistory": "Verlauf wird geladen …",
  "chat.emptyBody": "Schreib die erste Nachricht — dieser Chat hat seinen eigenen Kontext.",
  "chat.loadOlder": "Ältere Nachrichten laden",
  "chat.loadingOlder": "Wird geladen …",
  "chat.jumpLatest": "Zu den neuesten Nachrichten springen",
  "chat.composerPlaceholder": "Nachricht schreiben …",
  "chat.composerDisconnected": "Nicht verbunden",

  "steps.title": "Zwischenschritte",
  "steps.show": "Schritte anzeigen",
  "steps.hide": "Schritte ausblenden",
  "steps.options": "Schritte-Optionen",
  "steps.running": "läuft …",
  "steps.one": "1 Schritt",
  "steps.many": "{count} Schritte",
  "steps.thoughts": "Gedanken",
  "steps.toolOutput": "Werkzeug-Ausgabe",

  "bubble.running": "läuft …",
  "bubble.error": "Fehler",
  "bubble.more": "mehr",
  "bubble.less": "weniger",
  "bubble.notSent": "nicht gesendet",
  "bubble.sending": "sendet …",
  "bubble.workingPlain": "OpenClaw arbeitet …",
  "bubble.workingPhase": "OpenClaw arbeitet — {phase} …",
  "phase.queued": "in der Warteschlange",
  "phase.waiting_for_slot": "wartet auf einen freien Platz",
  "phase.provisioning_environment": "bereitet die Umgebung vor",
  "phase.preparing_context": "sammelt den Kontext",
  "phase.starting_model": "startet das Modell",

  "attachment.image": "Bild",
  "attachment.file": "Datei",
  "attachment.unavailable": "Bild nicht abrufbar",
  "attachment.loading": "Bild wird geladen …",
  "attachment.remove": "Entfernen",
  "attachment.previewImage": "📷 Bild",
  "attachment.previewFile": "📎 {name}",
  "attachment.redactedThinking": "(vom Anbieter zurückgehalten)",

  "composer.attach": "Bild anhängen",
  "composer.message": "Nachricht",
  "composer.stop": "Antwort stoppen",
  "composer.send": "Senden",
  "composer.queueLabel": "Wenn OpenClaw gerade arbeitet",
  "composer.steer": "Dazwischenreden",
  "composer.steerHint": "Fließt in den laufenden Zug ein",
  "composer.followup": "Anhängen",
  "composer.followupHint": "Kommt dran, wenn der Zug fertig ist",
  "composer.interrupt": "Unterbrechen",
  "composer.interruptHint": "Bricht den laufenden Zug ab und startet neu",

  "time.today": "Heute",
  "time.yesterday": "Gestern",

  "compose.title": "Neue Unterhaltung",
  "compose.description": "Jeder Chat ist eine eigene OpenClaw-Session mit eigenem Kontext.",
  "compose.titleLabel": "Titel (optional)",
  "compose.titlePlaceholder": "z. B. Urlaubsplanung",
  "compose.titleHint": "Ohne Titel benennt OpenClaw den Chat nach der ersten Nachricht.",
  "compose.messageLabel": "Erste Nachricht (optional)",
  "compose.messagePlaceholder": "Such mir Hotels für Prag.",
  "compose.submit": "Starten",
  "compose.submitting": "Wird erstellt …",

  "rename.title": "Chat umbenennen",
  "rename.label": "Titel",
  "rename.hint": "Leer lassen, um wieder den automatischen Titel zu verwenden.",

  "menu.rename": "Umbenennen",
  "menu.pin": "Anheften",
  "menu.unpin": "Nicht mehr anheften",
  "menu.archive": "Archivieren",
  "menu.restore": "Wiederherstellen",
  "menu.model": "Modell wechseln",
  "menu.speak": "Letzte Antwort vorlesen",
  "menu.delete": "Löschen",

  "delete.title": "Chat löschen?",
  "delete.description":
    "Die Unterhaltung und ihr Verlauf werden dauerhaft entfernt. Das lässt sich nicht rückgängig machen.",
  "delete.confirm": "Endgültig löschen",

  "action.cancel": "Abbrechen",
  "action.save": "Speichern",
  "action.copy": "kopieren",
  "action.copied": "kopiert",
  "action.copyToClipboard": "In die Zwischenablage kopieren",
  "action.close": "Schließen",

  "model.title": "Modell",
  "model.description": "Gilt nur für „{title}“.",
  "model.agentDefault": "Standard des Agenten",
  "model.agentDefaultMeta": "Kein festes Modell für diesen Chat",
  "model.loading": "Modelle werden geladen …",
  "model.none": "Dieses Gateway nennt keine auswählbaren Modelle.",
  "model.context": " · {size}k Kontext",
  "model.reasoning": " · denkt",
  "model.unavailable": " · nicht verfügbar",

  "settings.title": "Einstellungen",
  "settings.language": "Sprache",
  "settings.theme": "Design",
  "settings.themeSystem": "System",
  "settings.themeLight": "Hell",
  "settings.themeDark": "Dunkel",
  "settings.enter": "Enter-Taste",
  "settings.enterAuto": "Automatisch",
  "settings.enterSend": "Senden",
  "settings.enterNewline": "Zeilenumbruch",
  "settings.enterAutoHint":
    "Mit Tastatur sendet Enter, auf dem Touchscreen macht es einen Zeilenumbruch.",
  "settings.enterSendHint": "Enter sendet, Shift+Enter macht einen Zeilenumbruch.",
  "settings.enterNewlineHint": "Enter macht einen Zeilenumbruch, gesendet wird über den Knopf.",
  "settings.install": "App installieren",
  "settings.installed": "cllw läuft als installierte App.",
  "settings.installAction": "Auf dem Gerät installieren",
  "settings.installSafari":
    "In Safari unten auf <strong>Teilen</strong> tippen, dann <strong>Zum Home-Bildschirm</strong>. Auf dem iPhone ist das zugleich die Voraussetzung für Benachrichtigungen.",
  "settings.installManual":
    "Dieser Browser bietet keine Installation an — über sein Menü geht es oft trotzdem („Zum Startbildschirm“, „App installieren“).",
  "settings.notifications": "Benachrichtigungen",
  "settings.pushBusy": "Einen Moment …",
  "settings.pushEnable": "Benachrichtigungen einschalten",
  "settings.pushDisable": "Benachrichtigungen ausschalten",
  "settings.pushTest": "Testbenachrichtigung senden",
  "settings.pushHint":
    "Das Gateway schickt sie direkt an dieses Gerät — auch wenn cllw geschlossen ist.",
  "settings.pushUnsupported": "Dieser Browser kann keine Benachrichtigungen.",
  "push.setupTitle": "Dein Gateway ist dafür noch nicht eingerichtet",
  "push.setupWhat":
    "Verschickt werden die Benachrichtigungen von deinem Gateway, und dafür braucht es ein eigenes Signatur-Schlüsselpaar — den VAPID-Schlüssel. Es hat noch keinen, also kann der Browser den Absender nicht prüfen und lehnt die Anmeldung ab.",
  "push.setupAsk":
    "Am schnellsten von hier aus: OpenClaw fragen. Es darf seine eigene Konfiguration ändern — schreib ihm dort, wo du es ohnehin erreichst.",
  "push.setupPrompt":
    "Richte bitte Web Push auf dem Gateway ein: erzeuge ein VAPID-Schlüsselpaar, trag es in die Gateway-Konfiguration ein und starte danach das Gateway neu. Es hat geklappt, sobald push.web.vapidPublicKey den öffentlichen Schlüssel zurückgibt.",
  "push.setupAfter":
    "Beim Neustart ist das Gateway kurz weg. Danach noch einmal auf „Benachrichtigungen einschalten“ tippen.",
  "settings.gateway": "Gateway",
  "settings.status": "Status",
  "settings.statusConnected": "verbunden",
  "settings.scopes": "Rechte dieses Geräts",
  "settings.scopesHint":
    "Diese Rechte hast du beim Koppeln freigegeben. Ändern lässt sich das nur in OpenClaw selbst.",
  "settings.deviceId": "Geräte-ID",
  "settings.disconnect": "Verbindung trennen und Gerät abmelden",
  "scope.operator.read": "Chats und Verlauf lesen",
  "scope.operator.write": "Nachrichten senden, Chats verwalten",
  "scope.operator.admin": "Chats endgültig löschen",
  "scope.operator.pairing": "Geräte verwalten",
  "scope.operator.talk": "Sprach-Sitzungen",

  "update.available": "Eine neue Version ist verfügbar.",
  "update.reload": "Neu laden",

  "setup.lead":
    "Verbinde dich einmal mit deinem OpenClaw-Gateway. Danach bleibt die App angemeldet.",
  "setup.urlLabel": "Gateway-Adresse",
  "setup.urlHint":
    "Zum Beispiel <code>ws://192.168.1.20:18789</code> im eigenen Netz oder <code>wss://gateway.example</code> von unterwegs.",
  "setup.tokenLabel": "Token",
  "setup.tokenPlaceholder": "Gateway-Token",
  "setup.tokenHint":
    "Leer lassen, wenn dein Gateway ohne Token läuft. Dieses Gerät meldet sich anschließend als eigenes Gerät an — die Freigabe bestätigst du einmalig in OpenClaw.",
  "setup.submit": "Verbinden",
  "setup.submitting": "Verbinde …",

  "pairing.titleUpgrade": "Erweiterte Rechte freigeben",
  "pairing.title": "Gerät freigeben",
  "pairing.leadUpgrade":
    "Dieses Gerät meldet sich bei <strong>{url}</strong> mit geänderten Angaben an. Die Freigabe bestätigst du dafür noch einmal in OpenClaw.",
  "pairing.lead":
    "Die Verbindung zu <strong>{url}</strong> steht. Dieses Gerät wartet jetzt auf deine einmalige Freigabe in OpenClaw.",
  "pairing.step1": "Auf dem Gateway-Rechner ausführen",
  "pairing.noRequestId":
    "Das Gateway hat keine Request-ID mitgeschickt. Mit <code>openclaw devices list</code> siehst du die offene Anfrage.",
  "pairing.step2": "Fertig",
  "pairing.step2Hint":
    "Die App prüft alle paar Sekunden selbst und startet sofort, sobald du bestätigt hast.",
  "pairing.requestId": "Request-ID",
  "pairing.deviceId": "Geräte-ID",
  "pairing.requestedScopes": "Angefragte Rechte",
  "pairing.waiting": "Warte auf Freigabe …",
  "pairing.otherConnection": "Andere Verbindung",
  "pairing.checkNow": "Jetzt prüfen",

  "banner.pairing": "Warte auf Geräte-Freigabe in OpenClaw …",
  "banner.connecting": "Verbinde mit dem Gateway …",
  "banner.reconnecting": "Verbindung unterbrochen — versuche erneut …",
};

const PLACEHOLDER = /\{(\w+)\}/g;

export type TranslateVars = Record<string, string | number>;

export function translate(language: Language, key: MessageKey, vars?: TranslateVars): string {
  const template = (language === "de" ? DE[key] : undefined) ?? EN[key];
  if (!vars) {
    return template;
  }
  return template.replace(PLACEHOLDER, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

/** The BCP 47 tag `Intl` should format dates and times with. */
export function resolveLocale(language: Language): string {
  return language === "de" ? "de-DE" : "en-GB";
}
